/**
 * Proveedor Jitsi — clase en vivo EMBEBIDA (iframe External API).
 *
 * Sala DETERMINISTA `spaceacademy-{sessionId}-{hash}` con salt de servidor;
 * dominio configurable (default `meet.jit.si`). El componente embebido se carga
 * perezosamente (F3-G) para no arrastrar el SDK en el bundle base.
 */

import {
  DEFAULT_JITSI_DOMAIN,
  jitsiRoomName,
  type JoinDescriptor,
  type JoinUser,
  type LiveSession,
  type MeetingProvider,
  type MeetingProviderManifest,
} from "../registry";

const manifest: MeetingProviderManifest = {
  id: "jitsi",
  name: "Jitsi Meet",
  description: "Clase en vivo embebida (iframe) con sala determinista; sin credenciales.",
  capabilities: {
    embeddable: true,
    attendanceCallback: true, // eventos del iframe API (participantJoined/Left)
    recordingFetch: false, // v1: el profesor sube la grabación manualmente
    requiresCredentials: false,
  },
  configSchema: [
    {
      key: "domain",
      label: "Dominio Jitsi",
      type: "text",
      required: false,
      placeholder: DEFAULT_JITSI_DOMAIN,
      help: "Servidor Jitsi propio; por defecto meet.jit.si.",
      default: DEFAULT_JITSI_DOMAIN,
    },
  ],
};

export const jitsiProvider: MeetingProvider = {
  manifest,
  buildJoin(session: LiveSession, cfg: Record<string, unknown>, user: JoinUser): JoinDescriptor {
    const domainRaw =
      typeof cfg.domain === "string" && cfg.domain.trim() !== ""
        ? cfg.domain.trim()
        : DEFAULT_JITSI_DOMAIN;
    // El salt lo provee el servidor; sin él, la sala no es adivinable. Un salt
    // vacío degradaría el determinismo, así que exigimos su presencia.
    const salt = session.roomSalt ?? "";
    return {
      mode: "embed",
      provider: "jitsi",
      domain: domainRaw,
      roomName: jitsiRoomName(session.id, salt),
      displayName: user.name?.trim() || undefined,
    };
  },
  // Carga perezosa del componente de iframe (implementado en F3-G, features/live).
  // Aquí solo se declara el contrato; el consumidor real hará el import dinámico.
};
